/**
 * NfseHero — o hero sangrado do desenho oficial, na tela de Nota fiscal.
 *
 * ─── POR QUE ESTE É O ÚNICO HERO DO APP QUE VIROU COMPONENTE ────────────────
 *
 * Nas outras telas o hero é marcação inline da página, e está certo assim: cada
 * uma tem UM cabeçalho. `/fiscal` tem SEIS estados de tela — visitante, portão
 * de papel, portão de plano, carregando, autônomo sem CNPJ, readiness
 * incompleta — mais o módulo de emissão, e o módulo vive em outro arquivo
 * (`NfseModule`) porque é ele que carrega a lista e os diálogos. Inline, o mesmo
 * gradiente estaria escrito sete vezes em dois arquivos, e a primeira mudança
 * de desenho pegaria seis dos sete (é o #25 aplicado a um cabeçalho em vez de a
 * uma aba).
 *
 * ─── O VALOR AO VIVO É OPCIONAL, E ISSO É REGRA, NÃO CONVENIÊNCIA (#187) ────
 *
 * Nos estados de portão a lista de notas não é legível — ou o papel barra, ou o
 * plano barra, ou a empresa nem está apta a emitir. Um hero que imprimisse
 * "R$ 0,00" ali afirmaria, em `text-3xl`, que o prestador não faturou nada,
 * quando o que houve foi uma recusa. Sem `summary`, o bloco do valor
 * simplesmente NÃO É RENDERIZADO — a mesma cura do saldo da home.
 */
import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MODULE_LABELS } from '@/lib/labels';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { cn } from '@/lib/utils';

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

export interface NfseHeroSummary {
  /** O que a soma responde — é ele que torna o número honesto (#182/#188). */
  label: string;
  total: number;
  /** A linha de baixo: contagem do recorte e o que ficou fora da soma. */
  subline: string;
}

interface NfseHeroProps {
  onBack: () => void;
  /** Ação primária (só existe quando há o que fazer na tela). */
  action?: ReactNode;
  /** Ausente = a tela não consegue ler a lista; ver o cabeçalho. */
  summary?: NfseHeroSummary | null;
}

export function NfseHero({ onBack, action, summary }: NfseHeroProps) {
  return (
    <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

      <div className="relative flex items-center gap-2">
        {/* `/fiscal` não está na navegação inferior: sem este botão, a única
            saída da tela é o gesto do sistema. */}
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          onClick={onBack}
          aria-label="Voltar"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          {/* O NOME primeiro, a sigla como apoio. "NFS-e" é como o fisco chama o
              documento; "nota fiscal" é como o prestador chama. Um título que é
              só a sigla obriga a pessoa a saber o jargão para reconhecer a
              própria tela que ela veio procurar. A sigla desceu para o eyebrow,
              que nas outras telas diz a FAMÍLIA do documento — aqui ela diz as
              duas coisas de uma vez. */}
          <p className="text-[11px] uppercase tracking-wide text-white/70">
            Documentos · {MODULE_LABELS.invoice.acronym}
          </p>
          <h1 className="line-clamp-2 text-lg font-bold leading-tight">
            {MODULE_LABELS.invoice.one}
          </h1>
        </div>
        {action}
      </div>

      {summary && (
        <div className="relative mt-5 min-w-0">
          <p className="text-xs text-white/75">{summary.label}</p>
          {/* `heroTotalSizeClass` é a FONTE ÚNICA do degrau de tamanho (#159-b). */}
          <p
            className={cn(
              'truncate font-bold leading-tight',
              heroTotalSizeClass(formatCurrency(summary.total)),
            )}
          >
            {formatCurrency(summary.total)}
          </p>
          <p className="mt-0.5 text-xs text-white/70">{summary.subline}</p>
        </div>
      )}
    </div>
  );
}
